import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { formatDateTime } from '../../../utils/Utils';
import { csvFilterChip } from '../../../utils/csvFilterChip';

const SCOPE_OPTIONS = ['employment_records', 'health', 'communication', 'alumni'];
const STATE_OPTIONS = ['active', 'revoked', 'superseded'];

// Generic CSV-multi-select filter — used for `scope[in]` and `state[in]`.
// Value rendered as a comma-separated string in MUI DataTable's filterList,
// converted back to a multi-select when the popover opens.
function csvMultiFilter(label, options, t, optionLabel = (o) => o) {
  return {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(sel) => sel.map((s) => optionLabel(s)).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{optionLabel(o)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  };
}

const useKvkkConsentsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/kvkk-consents?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const revoke = useCallback(async (id, reason) => {
    try {
      await api.post(`/v1/kvkk-consents/${id}/revoke`, { reason: reason || 'admin_action' });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const scopeLabel = useCallback((s) => {
    switch (s) {
      case 'employment_records': return t('kvkkConsent.scope.employmentRecords');
      case 'health': return t('kvkkConsent.scope.health');
      case 'communication': return t('kvkkConsent.scope.communication');
      case 'alumni': return t('kvkkConsent.scope.alumni');
      default: return s;
    }
  }, [t]);

  const stateLabel = useCallback((s) => {
    switch (s) {
      case 'active': return t('kvkkConsent.state.active');
      case 'revoked': return t('kvkkConsent.state.revoked');
      case 'superseded': return t('kvkkConsent.state.superseded');
      default: return s;
    }
  }, [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      // Server param is `personName[like]`; body still pulls from the
      // DTO field `personFullName`.
      name: 'personName',
      label: t('kvkkConsent.personName'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (_, tableMeta) => data[tableMeta.rowIndex]?.personFullName || '-',
      },
    },
    {
      name: 'personEmail',
      label: t('kvkkConsent.personEmail'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'scope',
      label: t('kvkkConsent.scope.label'),
      options: {
        filter: true, sort: false,
        filterType: 'custom',
        customBodyRender: (v) => scopeLabel(v),
        customFilterListOptions: csvFilterChip(t('kvkkConsent.scope.label'), scopeLabel),
        filterOptions: csvMultiFilter(t('kvkkConsent.scope.label'), SCOPE_OPTIONS, t, scopeLabel),
      },
    },
    {
      name: 'version',
      label: t('kvkkConsent.version'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'eq' },
      },
    },
    {
      // Column name == server query param. Body rendered from sibling
      // fields on the row (revokedAt + revokeReason) since the API doesn't
      // emit a literal `state` column.
      name: 'state',
      label: t('kvkkConsent.state.label'),
      options: {
        filter: true, sort: false,
        filterType: 'custom',
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return '-';
          if (row.revokeReason === 'superseded') return stateLabel('superseded');
          if (row.revokedAt) return stateLabel('revoked');
          return stateLabel('active');
        },
        customFilterListOptions: csvFilterChip(t('kvkkConsent.state.label'), stateLabel),
        filterOptions: csvMultiFilter(t('kvkkConsent.state.label'), STATE_OPTIONS, t, stateLabel),
      },
    },
    dateFilterColumn('grantedAt', t('kvkkConsent.grantedAt'), lang, { includeTime: true }),
    {
      name: 'revokedAt',
      label: t('kvkkConsent.revokedAt'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v ? formatDateTime(v, lang) : '-',
      },
    },
  ], [t, lang, data, scopeLabel, stateLabel]);

  return { fetching, data, pagination, columns, refresh, revoke };
};

export default useKvkkConsentsApi;
